import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import OrganisationService from '../../../services/OrganisationService/OrganisationService';
import BotBuilderService from '../../../services/BotBuilderService/BotBuilderService';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useEffectiveOrganisationId from '../../../hooks/useEffectiveOrganisationId';
import useAuthRoles from '../../../hooks/useAuthRoles';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';
import CustomInput from '../../../components/CustomInput/CustomInput';
import Button from '../../../components/Button/Button';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import DeleteOrganisationModal from '../../../components/DeleteOrganisationModal/DeleteOrganisationModal';

function GeneralTab(): React.ReactElement {
  const organisationId = useEffectiveOrganisationId();
  const { setSelectedOrganisation, setCurrentChatbot } =
    React.useContext(ChatbotContext);
  const { userIsSuperAdmin } = useAuthRoles();
  const navigate = useNavigate();

  const [name, setName] = React.useState('');
  const [savedName, setSavedName] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [chatbotNames, setChatbotNames] = React.useState<string[]>([]);
  const [chatbotsLoaded, setChatbotsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!organisationId) return;

    OrganisationService.getOrganisation(organisationId)
      .then((organisation) => {
        setSavedName(organisation.name);
        setName(organisation.name);
      })
      .catch(() => {
        setErrorMessage('Unable to load the organisation');
        setShowError(true);
      });
  }, [organisationId]);

  React.useEffect(() => {
    if (!organisationId || !userIsSuperAdmin()) return;

    setChatbotsLoaded(false);
    BotBuilderService.getChatbotsInfo(organisationId)
      .then((chatbots) => {
        setChatbotNames(chatbots.map((chatbot) => chatbot.name));
        setChatbotsLoaded(true);
      })
      .catch(() => {
        setChatbotsLoaded(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const trimmedName = name.trim();
  const isUnchanged = trimmedName === savedName;

  const handleSave = async (): Promise<void> => {
    if (!organisationId || !trimmedName || isUnchanged) return;

    setShowError(false);
    setShowSuccess(false);

    try {
      setIsLoading(true);
      const updated = await OrganisationService.updateOrganisation(
        organisationId,
        trimmedName
      );
      setSavedName(updated.name);
      setName(updated.name);
      setSelectedOrganisation((current) =>
        current?.id === organisationId
          ? { ...current, name: updated.name }
          : current
      );
      setSuccessMessage('Organisation name updated');
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'Something went wrong');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrganisation = async (): Promise<void> => {
    if (!organisationId) return;

    await OrganisationService.deleteOrganisation(organisationId);

    try {
      const { results } = await OrganisationService.searchOrganisations('');
      const nextOrganisation =
        results.find((organisation) => organisation.id !== organisationId) ??
        null;

      if (nextOrganisation) {
        const chatbots = await BotBuilderService.getChatbotsInfo(
          nextOrganisation.id
        );
        setSelectedOrganisation(nextOrganisation);
        await setCurrentChatbot(chatbots.length > 0 ? chatbots[0].id : null);
      } else {
        await setCurrentChatbot(null);
        setSelectedOrganisation(null);
      }
    } catch {
      await setCurrentChatbot(null);
      setSelectedOrganisation(null);
    }

    navigate('/chats');
  };

  return (
    <div className='flex flex-1 flex-col'>
      <div className='w-full max-w-md'>
        <div className='space-y-2'>
          <LabelWithInfo
            htmlFor='organisation-name'
            info='The name used to identify your organisation.'
          >
            Organisation name
          </LabelWithInfo>
          <CustomInput
            id='organisation-name'
            name='organisationName'
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='flex justify-end mt-4'>
          <Button
            type='button'
            loading={isLoading}
            disabled={!trimmedName || isUnchanged}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      </div>

      {userIsSuperAdmin() && (
        <div className='w-full max-w-md border-t mt-8 pt-6'>
          <h3 className='text-sm font-semibold text-red-700'>Danger Zone</h3>
          <div className='flex items-center justify-between gap-4 mt-3'>
            <p className='text-sm text-muted-foreground'>
              Delete this organisation and all its data. This cannot be undone.
            </p>
            <Button
              type='button'
              variant='destructive'
              disabled={!chatbotsLoaded}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete organisation
            </Button>
          </div>
        </div>
      )}

      <DeleteOrganisationModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        organisationName={savedName}
        chatbotNames={chatbotNames}
        onConfirm={handleDeleteOrganisation}
      />

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </div>
  );
}

export default GeneralTab;
